import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar, X } from "lucide-react";
import CalendarGrid from "./CalendarGrid";
import {
  formatDisplayDate,
  getDefaultPickerMaxYear,
  getDefaultPickerMinYear,
  getDobMaxDate,
  getDobMinDate,
  parseDateValue,
  toDateValue,
  validateDateOfBirth,
} from "./datePickerUtils";

const INPUT_CLASS =
  "flex items-center gap-2 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white text-left hover:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500";

function defaultBirthViewDate() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 25);
  return d;
}

function getPortalRoot() {
  if (typeof document === "undefined") return null;
  let root = document.getElementById("datepicker-portal-root");
  if (!root) {
    root = document.createElement("div");
    root.id = "datepicker-portal-root";
    document.body.appendChild(root);
  }
  return root;
}

export default function DatePickerField({
  label,
  value = "",
  onChange,
  required = false,
  placeholder = "Pick a date",
  min,
  max,
  className = "",
  inputClassName,
  compact = false,
  hideLabel = false,
  disabled = false,
  error: externalError,
  /** Optimized for date of birth: year/month dropdowns, opens ~25y ago, max today */
  birthdate = false,
  /** Map of "YYYY-MM-DD" → { type, label } for calendar day markers */
  dayMarkers,
}) {
  const [open, setOpen] = useState(false);
  const [localError, setLocalError] = useState("");
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const portalRootRef = useRef(null);

  const selected = parseDateValue(value);
  const today = new Date();
  const effectiveMin =
    min ?? (birthdate ? toDateValue(getDobMinDate()) : min);
  const effectiveMax =
    max ?? (birthdate ? toDateValue(getDobMaxDate()) : max);
  const defaultView = selected || (birthdate ? defaultBirthViewDate() : today);
  const [viewYear, setViewYear] = useState(defaultView.getFullYear());
  const [viewMonth, setViewMonth] = useState(defaultView.getMonth());

  const minYear = effectiveMin
    ? parseDateValue(effectiveMin)?.getFullYear()
    : (birthdate ? getDobMinDate().getFullYear() : getDefaultPickerMinYear(today));
  const maxYear = effectiveMax
    ? parseDateValue(effectiveMax)?.getFullYear()
    : (birthdate ? today.getFullYear() : getDefaultPickerMaxYear(today));

  useEffect(() => {
    portalRootRef.current = getPortalRoot();
  }, []);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;

    const place = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const popupH = popupRef.current?.offsetHeight || 380;
      const popupW = popupRef.current?.offsetWidth || 300;
      const gap = 8;
      let top = rect.bottom + gap;
      if (top + popupH > window.innerHeight - 12) {
        top = Math.max(12, rect.top - popupH - gap);
      }
      // Center the calendar under the field
      let left = rect.left + rect.width / 2 - popupW / 2;
      if (left < 12) left = 12;
      if (left + popupW > window.innerWidth - 12) {
        left = Math.max(12, window.innerWidth - popupW - 12);
      }
      setPopupPos({ top, left });
    };

    place();
    // Remeasure after paint so real popup size is used for true centering
    const raf = requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, viewYear, viewMonth]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const inRoot = rootRef.current?.contains(e.target);
      const inPopup = popupRef.current?.contains(e.target);
      if (!inRoot && !inPopup) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (selected) {
      setViewYear(selected.getFullYear());
      setViewMonth(selected.getMonth());
    } else if (birthdate && open) {
      const dv = defaultBirthViewDate();
      setViewYear(dv.getFullYear());
      setViewMonth(dv.getMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, open, birthdate]);

  useEffect(() => () => setOpen(false), []);

  const inRange = (date) => {
    const v = toDateValue(date);
    if (effectiveMin && v < effectiveMin) return false;
    if (effectiveMax && v > effectiveMax) return false;
    return true;
  };

  const pickDate = (date) => {
    if (!inRange(date)) return;
    const next = toDateValue(date);
    if (birthdate) {
      const dobError = validateDateOfBirth(next);
      if (dobError) {
        setLocalError(dobError);
        return;
      }
    }
    setLocalError("");
    onChange?.(next);
    setOpen(false);
  };

  useEffect(() => {
    if (!birthdate) {
      setLocalError("");
      return;
    }
    if (!value) {
      setLocalError("");
      return;
    }
    setLocalError(validateDateOfBirth(value) || "");
  }, [value, birthdate]);

  const setToday = () => {
    pickDate(today);
  };

  const btnClass =
    inputClassName ||
    (compact
      ? "flex items-center gap-1.5 flex-1 min-w-0 border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white text-left hover:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
      : `${INPUT_CLASS} flex-1 min-w-0`);

  const popup = (
    <div
      ref={popupRef}
      className="fixed z-[9999] bg-white rounded-2xl border border-slate-200 shadow-2xl p-4"
      style={{
        top: popupPos.top,
        left: popupPos.left,
        transform: "translateX(0)",
      }}
    >
      <CalendarGrid
        viewYear={viewYear}
        viewMonth={viewMonth}
        selectedDate={selected}
        onSelectDate={pickDate}
        onViewMonthChange={(y, m) => {
          setViewYear(y);
          setViewMonth(m);
        }}
        showYearMonthSelect
        minYear={minYear}
        maxYear={maxYear}
        isDateDisabled={(date) => !inRange(date)}
        dayMarkers={dayMarkers}
      />
      <div className="flex justify-between gap-2 mt-3 pt-3 border-t border-slate-100">
        <button
          type="button"
          onClick={() => onChange?.("")}
          className="text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-50"
        >
          Clear
        </button>
        {birthdate ? (
          <span className="text-xs text-slate-400 self-center">Select year &amp; month above</span>
        ) : (
          <button
            type="button"
            onClick={setToday}
            className="text-xs font-medium text-teal-700 px-3 py-1 rounded-lg bg-teal-50 hover:bg-teal-100"
          >
            Today
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      {label && !hideLabel && (
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {label}
          {required ? " *" : ""}
        </label>
      )}
      <div className={`flex items-center gap-1 w-full ${compact ? "" : ""}`}>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={`${btnClass} inline-flex items-center gap-2 text-left ${!value ? "text-slate-400" : "text-slate-800"} ${externalError || localError ? "border-red-400 focus:ring-red-200" : ""} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          <Calendar className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} text-teal-600 shrink-0`} />
          <span className="flex-1 truncate min-w-0">{value ? formatDisplayDate(value) : placeholder}</span>
        </button>
        {value ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange?.("")}
            className="shrink-0 p-2 rounded-lg border border-slate-200 bg-white text-slate-400 hover:bg-slate-50 hover:text-slate-600 disabled:opacity-60"
            aria-label="Clear date"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>

      {!externalError && localError ? (
        <p className="mt-1 text-xs text-red-600">{localError}</p>
      ) : birthdate && !externalError && !compact ? (
        <p className="mt-1 text-xs text-slate-500">
          Age must be below 100 years. Year must be 4 digits.
        </p>
      ) : null}

      {open && portalRootRef.current ? createPortal(popup, portalRootRef.current) : null}
    </div>
  );
}
